// Tiny reCAPTCHA v3 helper — lazy-loads the Google script once and exposes a Promise-based
// execute(action) that returns the token. We use v3 (invisible, score-based) on the public
// /apply pages so we can gate spam without making the applicant solve a checkbox / image puzzle.
//
// Loaded only when the public surface needs it; never on authenticated pages so we don't pay the
// script cost (or send the user's session into Google's referer chain) where it isn't required.
//
// Returns null when the site key isn't configured (dev / preview environments) so the calling
// code can degrade gracefully — backend has a corresponding "captcha disabled" env flag for
// matching dev bypass.

const RECAPTCHA_SCRIPT_ID = 'recaptcha-v3-script'
const SITE_KEY = import.meta?.env?.VITE_GOOGLE_RECAPTCHA_SITE_KEY || ''

let scriptPromise = null

function loadScript() {
  if (typeof window === 'undefined') return Promise.resolve(null)
  if (!SITE_KEY) return Promise.resolve(null)
  if (scriptPromise) return scriptPromise
  if (window.grecaptcha && window.grecaptcha.execute) {
    scriptPromise = Promise.resolve(window.grecaptcha)
    return scriptPromise
  }
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(RECAPTCHA_SCRIPT_ID)
    if (existing) {
      existing.addEventListener('load', () => resolve(window.grecaptcha))
      existing.addEventListener('error', () => reject(new Error('recaptcha-v3 load failed')))
      return
    }
    const script = document.createElement('script')
    script.id = RECAPTCHA_SCRIPT_ID
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(SITE_KEY)}`
    script.async = true
    script.defer = true
    script.onload = () => {
      // grecaptcha.ready guarantees the global is fully initialised before we call execute.
      window.grecaptcha?.ready(() => resolve(window.grecaptcha))
    }
    script.onerror = () => reject(new Error('recaptcha-v3 load failed'))
    document.head.appendChild(script)
  })
  return scriptPromise
}

/**
 * Run a v3 challenge for `action` and return the token string. Returns null when reCAPTCHA isn't
 * configured (dev) — caller should pass null straight through; the backend's captcha-disabled
 * flag will accept the missing token in matching env modes.
 */
export async function executeRecaptchaV3(action) {
  try {
    const grecaptcha = await loadScript()
    if (!grecaptcha || !SITE_KEY) return null
    return await grecaptcha.execute(SITE_KEY, { action })
  } catch {
    return null
  }
}

export function isRecaptchaConfigured() {
  return Boolean(SITE_KEY)
}
